import { Chip, Box } from "@mui/material";
import PropTypes from "prop-types";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

/**
 * StatusPill — the generic rounded status pill from the click-dummy.
 *
 * It renders an MUI Chip using one of the status `variant`s registered in the
 * theme (see muiTheme.js Chip variants), so the colour/tone comes from the
 * design system. Domain badges (ClaimStatusBadge, PaymentImportStatusBadge,
 * BookingStatus) are thin wrappers that map their status → variant and delegate
 * here, keeping the pill markup/behaviour (optional dropdown chevron, sizes,
 * a11y) in one place.
 *
 * @param {string} variant - Theme Chip variant, e.g. "claim-status-pruefung".
 * @param {React.ReactNode} label - Display label.
 * @param {'tiny'|'small'|'medium'} [size]
 * @param {function} [onClick] - When set, renders a dropdown chevron.
 * @param {boolean} [open] - Chevron direction for dropdown usage.
 * @param {boolean} [disabled]
 * @param {string} [ariaLabelledby]
 */
export default function StatusPill({
  variant,
  label,
  size = "small",
  onClick,
  open = false,
  disabled = false,
  ariaLabelledby,
}) {
  const tinySx =
    size === "tiny"
      ? {
          fontSize: "0.7rem",
          height: 20,
        }
      : {};

  return (
    <Chip
      label={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span aria-labelledby={ariaLabelledby}>{label}</span>
          {onClick &&
            !disabled &&
            (open ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            ))}
        </Box>
      }
      variant={variant}
      size={size === "tiny" ? "small" : size}
      sx={tinySx}
      onClick={!disabled ? onClick : undefined}
      disabled={disabled}
    />
  );
}

StatusPill.propTypes = {
  variant: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  size: PropTypes.oneOf(["tiny", "small", "medium"]),
  onClick: PropTypes.func,
  open: PropTypes.bool,
  disabled: PropTypes.bool,
  ariaLabelledby: PropTypes.string,
};
